<template>
  <div
    class="flex items-center gap-2 border-1 rounded-lg p-3 transition-100"
    :class="error ? 'border-critical-500 bg-critical-50' : [displayConfig?.cardBorderClass, 'cursor-pointer bg-white']"
    @click="!error && emit('click')"
  >
    <div
      class="flex flex-row items-center justify-center w-[35px] h-[42px] border-1 shadow-sm rounded-md shrink-0 bg-white"
      :class="error ? 'border-critical-300' : 'border-secondary-400'"
    >
      <AwSkeleton v-if="isLoading" class="h-4 w-4 rounded-md"></AwSkeleton>
      <AwIcon v-else-if="error" name="i-ph-warning" class="text-md text-critical-600" />
      <AwIcon v-else-if="displayConfig" name="i-ph-file-text" class="text-md" :class="displayConfig.fileIconClass" />
    </div>

    <div class="flex flex-col gap-1.5 min-w-0 flex-1">
      <AwSkeleton v-if="isLoading" class="h-4 w-24 rounded-full"></AwSkeleton>
      <div v-else class="text-md font-semibold truncate">
        {{ error ? $t("documents.recordCard.notFound") : documentName }}
      </div>

      <AwSkeleton v-if="isLoading" class="h-6 w-16 rounded-full mt-1"></AwSkeleton>
      <AwBadge
        v-else-if="error"
        :label="$t('documents.recordCard.error')"
        leading-icon="i-ph-x-circle-fill"
        color="critical"
        is-rounded
        class="self-start"
      />
      <AwBadge
        v-else-if="replaced"
        :label="$t('documents.recordCard.archived')"
        leading-icon="i-ph-archive"
        color="secondary"
        is-rounded
        class="self-start"
      />
      <DocumentStatusBadge
        v-else
        class="self-start"
        :lifecycle-status="lifecycleStatus"
        :days-before-expiration="daysBeforeExpiration"
        :expiration-date="expirationDate"
        :internal-reviews="internalReviews"
        :external-reviews="externalReviews"
      />
    </div>

    <div v-if="!error" class="flex items-center gap-1.5 ml-auto">
      <!-- Verify button (not validated only) -->
      <AwButton
        v-if="displayConfig?.showVerify && !replaced && !readonly"
        :label="$t('documents.recordCard.verify')"
        size="sm"
        color="critical"
        variant="solid"
        @click.stop="emit('verify')"
      />

      <!-- Retry button (failed only) -->
      <AwButton
        v-if="displayConfig?.showRetry && !replaced && !readonly"
        :label="$t('documents.recordCard.retry')"
        size="sm"
        color="secondary"
        variant="solid"
        @click.stop="emit('verify')"
      />

      <!-- Replace button (single type) -->
      <AwButton
        v-if="displayConfig?.showReplace && documentType && !replaced && !readonly"
        :label="$t('documents.recordCard.replace')"
        size="sm"
        :color="displayConfig?.replaceButtonColor"
        variant="solid"
        @click.stop="emit('replace', documentType)"
      />

      <!-- Replace dropdown (multiple types) -->
      <AwDropdownMenu
        v-else-if="displayConfig?.showReplace && allowedDocumentTypes && !replaced && !readonly"
        :items="
          allowedDocumentTypes.map((type) => ({
            label: getHandler(type).documentName,
            onClick: () => emit('replace', type),
          }))
        "
      >
        <AwButton
          :label="$t('documents.recordCard.replaceWith')"
          size="sm"
          :color="displayConfig?.replaceButtonColor"
          variant="solid"
          trailing-icon="i-ph-caret-down"
          @click.stop
        />
      </AwDropdownMenu>

      <!-- Download button (validated only) -->
      <AwButton
        v-if="readonly"
        leading-icon="i-ph-eye"
        size="sm"
        color="secondary"
        variant="outline"
        @click.stop="emit('click')"
      />

      <AwButton
        v-if="displayConfig?.showDownload"
        leading-icon="i-ph-download-simple"
        size="sm"
        color="secondary"
        variant="outline"
        @click.stop="emit('download')"
      />

      <AwButton
        v-if="displayConfig?.showDelete && allowDelete && !replaced && !readonly"
        leading-icon="i-ph-trash"
        size="sm"
        color="secondary"
        variant="outline"
        @click.stop="emit('remove')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getHandler } from "../handlers/registry";
import DocumentStatusBadge from "./DocumentStatusBadge.vue";
import { getDisplayConfig } from "./getDisplayConfig";
import type { DocumentType } from "../handlers/types";
import type { Props as DocumentStatusBadgeProps } from "./DocumentStatusBadge.vue";

type BaseProps = {
  documentName?: string;
  isLoading: boolean;
  error?: string | null;
  documentType?: DocumentType;
  allowedDocumentTypes?: DocumentType[];
  allowDelete?: boolean;
  replaced?: boolean;
  readonly?: boolean;
};

type Props = BaseProps & DocumentStatusBadgeProps;

type Emits = {
  click: [];
  replace: [documentType: DocumentType];
  verify: [];
  remove: [];
  download: [];
};

const props = withDefaults(defineProps<Props>(), {
  documentName: undefined,
  lifecycleStatus: undefined,
  daysBeforeExpiration: undefined,
  expirationDate: undefined,
  internalReviews: () => [],
  externalReviews: () => [],
  error: undefined,
  documentType: undefined,
  allowedDocumentTypes: undefined,
  allowDelete: true,
  replaced: false,
  readonly: false,
});

const emit = defineEmits<Emits>();

const displayConfig = computed(() => getDisplayConfig(props.lifecycleStatus));
</script>
